import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Plus, Shield } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { EmptyState } from '../../components/common/EmptyState';
import { LiabilityFormDialog } from '../../components/liabilities/LiabilityFormDialog';
import { LiabilityRow } from '../../components/liabilities/LiabilityRow';
import { DebtLibertyHero } from '../../components/liabilities/DebtLibertyHero';
import { DebtPlanLinkCard } from '../../components/liabilities/DebtPlanLinkCard';
import { Button } from '../../components/ui/Button';
import {
  useActiveRepaymentPlanQuery,
  useCreateLiabilityMutation,
  useLiabilitiesQuery,
  useRepaymentPlanQuery,
  useUpdateLiabilityMutation,
} from '../../features/liabilities/queries';
import pt from '../../locales/pt';
import { extractMutationError } from '../../utils/apiError';
import {
  StickyPageAction,
  StickyPageActionButton,
} from '../../components/layout/StickyPageAction';
import { MOBILE_PAGE_PADDING_WITH_STICKY_CLASS } from '../../components/layout/mobileLayoutConstants';
import { SectionSkeleton } from '../../components/common/SectionSkeleton';
import { PageContentShell } from '../../components/common/PageContentShell';
import { useQueryShellState } from '../../hooks/useQueryShellState';
import { cn } from '../../utils/cn';
import { GuidedFlowReturnBanner } from '../../components/dashboard/GuidedFlowReturnBanner';
import {
  aggregateLiberation,
  formatFreeEta,
} from '../../lib/debtDisplayUtils';
import {
  formatPayoffDatePt,
  predictedPayoffDateFromPlan,
} from '../../utils/debtPlanUtils';
import { paths } from '../../routes/paths';

export function Dividas() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: liabilities = [], isPending, isFetching } = useLiabilitiesQuery();
  const listShell = useQueryShellState({ data: liabilities, isPending, isFetching });
  const { data: activePlan } = useActiveRepaymentPlanQuery();
  const planMethod = activePlan?.method || 'avalanche';
  const { data: orderedPlan } = useRepaymentPlanQuery(planMethod, {
    enabled: Boolean(activePlan?.method),
  });
  const createMutation = useCreateLiabilityMutation();
  const updateMutation = useUpdateLiabilityMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (location.state?.openPlanWizard) {
      navigate(paths.dividasPlan, { replace: true });
    }
  }, [location.state, navigate]);

  const active = useMemo(
    () => liabilities.filter((l) => !l.is_archived && !l.is_paid_off),
    [liabilities],
  );

  const orderedActive = useMemo(() => {
    if (!active.length) return [];
    const items = orderedPlan?.items || activePlan?.plan_snapshot?.items || [];
    if (!items.length) return active;
    const byId = new Map(active.map((l) => [String(l.id), l]));
    const ordered = [];
    for (const item of items) {
      const id = String(item.liability_id || item.id);
      const row = byId.get(id);
      if (row) {
        ordered.push(row);
        byId.delete(id);
      }
    }
    for (const leftover of byId.values()) ordered.push(leftover);
    return ordered;
  }, [active, orderedPlan, activePlan]);

  const liberation = useMemo(() => aggregateLiberation(active), [active]);

  const freeEtaLabel = useMemo(() => {
    const iso = predictedPayoffDateFromPlan(activePlan);
    if (!iso) return '';
    return formatFreeEta(iso) || formatPayoffDatePt(iso);
  }, [activePlan]);

  const priorityId = useMemo(() => {
    if (activePlan?.priority_liability) return String(activePlan.priority_liability);
    const first = orderedPlan?.items?.[0] || activePlan?.plan_snapshot?.items?.[0];
    return first ? String(first.liability_id || first.id) : orderedActive[0]
      ? String(orderedActive[0].id)
      : null;
  }, [activePlan, orderedPlan, orderedActive]);

  const handleSubmit = async (payload) => {
    try {
      if (editing?.id) {
        await updateMutation.mutateAsync({ id: editing.id, payload });
        toast.success(pt.liabilities.toasts.updated);
      } else {
        await createMutation.mutateAsync(payload);
        toast.success(pt.liabilities.toasts.created);
      }
      setDialogOpen(false);
      setEditing(null);
    } catch (err) {
      toast.error(extractMutationError(err, pt.common.retry));
    }
  };

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  return (
    <div
      className={cn(
        'flex w-full min-w-0 flex-col gap-6 overflow-x-hidden',
        MOBILE_PAGE_PADDING_WITH_STICKY_CLASS,
      )}
    >
      <GuidedFlowReturnBanner />
      <header className="hidden md:flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-foreground">
            {pt.liabilities.title}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{pt.liabilities.subtitle}</p>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={openCreate}
          className="hidden shrink-0 md:inline-flex"
        >
          <Plus className="mr-1 h-4 w-4" aria-hidden />
          {pt.liabilities.addDebt}
        </Button>
      </header>

      <PageContentShell
        shells={[listShell]}
        fallback={<SectionSkeleton variant="card-grid" />}
      >
        {active.length === 0 ? (
          <EmptyState
            icon={Shield}
            title={pt.liabilities.empty.title}
            description={pt.liabilities.empty.subtitle}
            action={
              <Button
                onClick={() => {
                  setEditing(null);
                  setDialogOpen(true);
                }}
              >
                {pt.liabilities.empty.cta}
              </Button>
            }
          />
        ) : (
          <>
            <DebtLibertyHero
              pctPaid={liberation.pctPaid}
              hasProgress={liberation.hasProgress}
              paidAmount={liberation.paidAmount}
              remainingAmount={liberation.remainingAmount}
              freeEtaLabel={freeEtaLabel}
            />

            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-foreground">
                  {pt.liabilities.listTitle}
                </h2>
                <Button asChild variant="ghost" size="sm" className="text-xs">
                  <Link to={paths.dividasEvaluator}>{pt.liabilities.actions.evaluate}</Link>
                </Button>
              </div>
              {orderedActive.map((liability) => (
                <LiabilityRow
                  key={liability.id}
                  liability={liability}
                  isPriority={priorityId != null && String(liability.id) === priorityId}
                  onEdit={(row) => {
                    setEditing(row);
                    setDialogOpen(true);
                  }}
                />
              ))}
            </div>

            <DebtPlanLinkCard plan={activePlan} />

            {!activePlan ? (
              <Button asChild variant="secondary">
                <Link to={paths.dividasPlan}>{pt.liabilities.actions.generatePlan}</Link>
              </Button>
            ) : null}
          </>
        )}
      </PageContentShell>

      <LiabilityFormDialog
        isOpen={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
        initialValues={editing}
        isPending={createMutation.isPending || updateMutation.isPending}
        onSubmit={handleSubmit}
      />

      <StickyPageAction>
        <StickyPageActionButton onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" aria-hidden />
          {pt.liabilities.addDebt}
        </StickyPageActionButton>
      </StickyPageAction>
    </div>
  );
}

export default Dividas;
